import { FormControl, FormHelperText } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { Calendar } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useApi } from "../../contexts/ApiContext";
import FormLabel from "../FormLabel";
import { InputProps } from "./InputProps";
import { colors } from "../../theme";

export type DateInputPropsType = InputProps;

interface DateInputProps
  extends Omit<Omit<DateInputPropsType, "onChange">, "value"> {
  onChange: (value: Date | null) => void;
  value: Date;
  id?: string;
  defaultValue?: Date;
}

const MonthInput: React.FC<DateInputProps> = ({
  label,
  value,
  onChange,
  error,
  id,
  helperText,
}) => {
  const [open, setOpen] = useState(false);
  const [cleared, setCleared] = React.useState<boolean>(false);

  const { isSubmitting, isLoading } = useApi();
  const isDisabled = isLoading || isSubmitting;

  const handleDateChange = (newValue: Date | null) => {
    onChange(newValue);
  };

  const handleClear = () => {
    setCleared(true);
    onChange(null);
  };

  useEffect(() => {
    if (cleared) {
      const timeout = setTimeout(() => setCleared(false), 1500);
      return () => clearTimeout(timeout);
    }
  }, [cleared]);

  React.useEffect(() => {
    if (cleared) {
      const timeout = setTimeout(() => {
        setCleared(false);
      }, 1500);

      onChange(null);

      return () => clearTimeout(timeout);
    }
    return () => {};
  }, [cleared]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <FormControl disabled={isDisabled} error={error} id={id} fullWidth>
        <FormLabel error={error}>{label}</FormLabel>

        <DesktopDatePicker
          disabled={isDisabled}
          views={["year", "month"]}
          sx={{ width: 260 }}
          onOpen={() => setOpen(true)}
          onChange={handleDateChange}
          onClose={() => setOpen(false)}
          open={open}
          value={value ?? null}
          slots={{
            openPickerIcon: Calendar,
          }}
          slotProps={{
            openPickerButton: {
              color: "secondary",
              sx: { color: "rgba(0,0,0,0.5)" },
            },
            field: {
              format: "MM/yyyy",
              clearable: true,
              onClear: handleClear,
              sx: { width: "100%", border: error ? `1px solid ${colors.red[500]}` : "none" },
            },
          }}
        />
        {error && helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    </LocalizationProvider>
  );
};

export default MonthInput;
